if (!customElements.get("product-configurator")) {
  class ProductConfigurator extends HTMLElement {
    constructor() {
      super();
      this.selectedVariant = null;
      this.additionalProducts = [];
      this.selectedAdditionalVariants = new Map();
      this.baseProductPrice = 0;
      this.currentStep = 1;
      this.isSubmitting = false;
      this.priceFormatter = null;
    }

    connectedCallback() {
      // Cache DOM elements
      this.form = this.querySelector("form");
      this.productIdInput = document.getElementById("productID");
      this.additionalProductsInputsContainer = document.getElementById(
        "additionalProductsInputs",
      );
      this.nav = document.getElementById("configuratorNav");
      this.navTabs = document.querySelectorAll(".configurator__nav__tab");
      this.stepsWrapper = document.getElementById("configuratorSteps");
      this.steps = document.querySelectorAll(".configurator__step");
      this.totalPriceElement = document.getElementById("totalPrice");
      this.greenleazeButton = document.getElementById(
        "greenleaze-configurator-button",
      );
      this.purchasePriceSmallElement = document.getElementById(
        "configuratorPurchasePrice",
      );
      this.productImage = document.getElementById("configuratorProductImage");
      this.additionalProductsWrapper = document.querySelector(
        ".configurator__additional-product-wrapper",
      );

      // Configurator type (e.g., "Leasing")
      this.configuratorType = this.getAttribute("data-configurator-type") || "";

      // Next button (to change label on last step in Leasing mode)
      this.nextButton = this.querySelector("product-configurator-next-button");
      this.nextButtonLabelElement =
        this.nextButton && this.nextButton.querySelector(".content");
      this.defaultNextLabel =
        (this.nextButtonLabelElement &&
          this.nextButtonLabelElement.textContent.trim()) ||
        "";

      // Validate required elements
      if (!this.#validateRequiredElements()) {
        return;
      }

      // Initialize price formatter
      const currency = this.getAttribute("data-currency") || "EUR";
      const locale = this.getAttribute("data-locale") || "fr-FR";
      this.priceFormatter = new Intl.NumberFormat(locale, {
        style: "currency",
        currency: currency,
      });

      // Initialize base product price from data attribute or PRODUCT global
      this.#initializeBasePrice();

      // Set CSS custom properties
      this.nav.style.setProperty("--number-of-steps", this.navTabs.length);

      // Set initial ARIA attributes
      this.#initializeAccessibility();

      this.#buildOptionIndexMap();
      this.updateSelectedVariant();
    }

    #handleize(str) {
      return (str || "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
    }

    #buildOptionIndexMap() {
      this.optionIndexByName = new Map();
      if (typeof PRODUCT !== "undefined" && Array.isArray(PRODUCT.options)) {
        PRODUCT.options.forEach((name, index) => {
          this.optionIndexByName.set(this.#handleize(name), index);
        });
      }
    }

    #variantMatches(selections, v) {
      for (const [name, value] of selections) {
        const idx = this.optionIndexByName?.get(name);
        if (idx === undefined) continue;
        const vOption = ((v.options || [])[idx] || "").toLowerCase().trim();
        if (vOption !== value) return false;
      }
      return true;
    }

    #validateRequiredElements() {
      return !!(
        this.form &&
        this.productIdInput &&
        this.additionalProductsInputsContainer &&
        this.nav &&
        this.totalPriceElement &&
        this.productImage
      );
    }

    #initializeBasePrice() {
      const dataPrice = this.getAttribute("data-base-price");
      if (dataPrice) {
        this.baseProductPrice = Number(dataPrice);
      } else if (typeof PRODUCT !== "undefined" && PRODUCT.price) {
        this.baseProductPrice = Number(PRODUCT.price);
      }
      this.#updateTotalPrice();
    }

    #initializeAccessibility() {
      // Add ARIA labels to navigation
      this.navTabs.forEach((tab, index) => {
        tab.setAttribute("role", "tab");
        tab.setAttribute("aria-selected", index === 0 ? "true" : "false");
        tab.setAttribute("tabindex", index === 0 ? "0" : "-1");
      });

      // Add ARIA labels to steps
      this.steps.forEach((step, index) => {
        step.setAttribute("role", "tabpanel");
        step.setAttribute("aria-hidden", index === 0 ? "false" : "true");
      });
    }

    updateStep(index) {
      if (index < 1 || index > this.navTabs.length) return;

      this.currentStep = index;
      this.#updateStepUI();
      this.#updateAccessibility();

      // Announce step change for screen readers
      this.#announceStepChange();
    }

    goToNextStep() {
      if (this.isSubmitting) return;

      if (this.currentStep === this.navTabs.length) {
        this.#submitForm();
      } else {
        this.updateStep(this.currentStep + 1);
      }
    }

    #submitForm() {
      // Validate before submit
      if (!this.selectedVariant) {
        return;
      }

      this.isSubmitting = true;
      this.#showNextButtonLoading();

      // Prepare form data
      const formData = new FormData(this.form);

      // Submit to cart and redirect to checkout
      fetch("/cart/add.js", {
        method: "POST",
        body: formData,
      })
        .then((response) => response.json())
        .then((data) => {
          // Redirect to checkout after successful cart addition
          window.location.href = "/checkout";
        })
        .catch(() => {
          this.#hideNextButtonLoading();
          // Fallback to regular form submission
          this.form.submit();
        });
    }

    #updateStepUI() {
      this.style.setProperty("--configurator-step", this.currentStep);

      this.navTabs.forEach((tab, index) => {
        const isActive = index + 1 <= this.currentStep;
        tab.classList.toggle("configurator__nav__tab--active", isActive);
      });

      this.steps.forEach((step, index) => {
        const isActive = index + 1 === this.currentStep;
        step.classList.toggle(
          "configurator__step--active",
          index + 1 <= this.currentStep,
        );
      });

      // Update CTA label depending on step and configurator type
      this.#updateNextButtonLabel();
    }

    #updateNextButtonLabel() {
      if (!this.nextButtonLabelElement) return;

      const type = this.configuratorType;
      const isLeasingOrLocation = type === "Leasing" || type === "Location";
      const isLastStep = this.currentStep === this.navTabs.length;

      if (isLeasingOrLocation && isLastStep) {
        this.nextButtonLabelElement.textContent = "Louer";
      } else {
        this.nextButtonLabelElement.textContent =
          this.defaultNextLabel || "Suivant";
      }
    }

    #updateAccessibility() {
      this.navTabs.forEach((tab, index) => {
        const isActive = index + 1 === this.currentStep;
        tab.setAttribute("aria-selected", isActive ? "true" : "false");
        tab.setAttribute("tabindex", isActive ? "0" : "-1");
      });

      this.steps.forEach((step, index) => {
        const isVisible = index + 1 === this.currentStep;
        step.setAttribute("aria-hidden", isVisible ? "false" : "true");
      });
    }

    #announceStepChange() {
      // Create or update live region for announcements
      let liveRegion = document.getElementById("configurator-live-region");
      if (!liveRegion) {
        liveRegion = document.createElement("div");
        liveRegion.id = "configurator-live-region";
        liveRegion.setAttribute("aria-live", "polite");
        liveRegion.setAttribute("aria-atomic", "true");
        liveRegion.className = "sr-only";
        document.body.appendChild(liveRegion);
      }

      liveRegion.textContent = `Étape ${this.currentStep} sur ${this.navTabs.length}`;
    }

    addAdditionalProduct(productId) {
      if (typeof ALL_ADDITIONAL_PRODUCTS === "undefined") {
        return;
      }

      const product = ALL_ADDITIONAL_PRODUCTS.find(
        (p) => p.id === Number(productId),
      );

      if (!product) {
        return;
      }

      // Prevent duplicates
      if (this.additionalProducts.some((p) => p.id === product.id)) {
        return;
      }

      this.additionalProducts.push(product);
      this.#updateAdditionalProductsUI();
      this.#updateAdditionalProductsInputs();
      this.#updateTotalPrice();
    }

    removeAdditionalProduct(productId) {
      this.additionalProducts = this.additionalProducts.filter(
        (product) => product.id !== Number(productId),
      );
      this.selectedAdditionalVariants.delete(Number(productId));
      this.#updateAdditionalProductsUI();
      this.#updateAdditionalProductsInputs();
      this.#updateTotalPrice();
    }

    updateAdditionalProductVariant(productId, variant) {
      this.selectedAdditionalVariants.set(Number(productId), variant);
      this.#updateAdditionalProductsInputs();
      this.#updateTotalPrice();
    }

    #updateAdditionalProductsUI() {
      if (!this.additionalProductsWrapper) return;

      // Use DocumentFragment for better performance
      const fragment = document.createDocumentFragment();

      this.additionalProducts.forEach((product) => {
        const container = this.#createAdditionalProductElement(product);
        fragment.appendChild(container);
      });

      this.additionalProductsWrapper.innerHTML = "";
      this.additionalProductsWrapper.appendChild(fragment);
    }

    #createAdditionalProductElement(product) {
      const container = document.createElement("div");
      container.classList.add("configurator__additional-product");

      const img = new Image();
      img.src = product.featured_image;
      img.alt = product.title;
      img.width = 40;
      img.height = 40;
      img.loading = "lazy";

      container.appendChild(img);
      return container;
    }

    #updateAdditionalProductsInputs() {
      if (!this.additionalProductsInputsContainer) return;

      // Use DocumentFragment for better performance
      const fragment = document.createDocumentFragment();

      this.additionalProducts.forEach((product, index) => {
        if (!product.variants || !product.variants[0]) {
          return;
        }

        const selectedVariant = this.selectedAdditionalVariants.get(product.id);
        const variantId = selectedVariant
          ? selectedVariant.id
          : product.variants[0].id;

        const input = document.createElement("input");
        input.type = "hidden";
        input.name = `items[${index + 1}][id]`;
        input.value = variantId;
        fragment.appendChild(input);
      });

      this.additionalProductsInputsContainer.innerHTML = "";
      this.additionalProductsInputsContainer.appendChild(fragment);
    }

    recalculateTotalPrice() {
      this.#updateTotalPrice();
    }

    updateSelectedVariant() {
      if (typeof PRODUCT === "undefined") {
        return;
      }

      const selectedOptions = this.querySelectorAll(
        ".configurator__option--option .configurator__option__input:checked",
      );

      if (selectedOptions.length === 0) return;

      const selectedValues = Array.from(selectedOptions).map((input) =>
        input.value.toLowerCase().trim(),
      );

      const selectedSelections = new Map();
      selectedOptions.forEach((input) => {
        selectedSelections.set(input.name, input.value.toLowerCase().trim());
      }); // <-- La parenthèse a bien été corrigée ici

      this.selectedVariant =
        this.#findMatchingVariant(selectedSelections, selectedValues);

      if (this.selectedVariant) {
        this.#updateProductImage();
        this.#updateBaseProductPrice();
        this.#updateProductIdInput();
        this.#updateUrlVariant();
        this.#updateOptionImages();

        // --- BLOC DATE DE LIVRAISON ---
        const datesElement = document.getElementById('variant-delivery-dates');
        const outputElement = document.getElementById('livraison_estimee');

        if (datesElement && outputElement) {
          try {
            const deliveryDates = JSON.parse(datesElement.textContent);
            const selectedDate = deliveryDates[this.selectedVariant.id]; 
            outputElement.textContent = selectedDate || "Non spécifiée";
          } catch (e) {
            console.error("Erreur lors de la mise à jour de la date : ", e);
          }
        }
      }

      this.#updateOptionAvailability();
    }

    #updateOptionAvailability() {
      if (typeof PRODUCT === "undefined" || !PRODUCT.variants) return;

      const allInputs = Array.from(
        this.querySelectorAll(
          ".configurator__option--option .configurator__option__input",
        ),
      );
      if (!allInputs.length) return;

      const selectedByName = new Map();
      allInputs.forEach((input) => {
        if (input.checked) {
          selectedByName.set(input.name, input.value.toLowerCase().trim());
        }
      });

      allInputs.forEach((input) => {
        const option = input.closest("product-configurator-option");
        if (!option) return;

        // Only filter groups that map to an actual variant dimension
        if (!this.optionIndexByName?.has(input.name)) {
          option.classList.remove("configurator__option--unavailable");
          return;
        }

        const thisValue = input.value.toLowerCase().trim();
        const testSelections = new Map(selectedByName);
        testSelections.set(input.name, thisValue);

        const hasVariant = PRODUCT.variants.some(
          (v) =>
            v.available !== false && this.#variantMatches(testSelections, v),
        );

        option.classList.toggle(
          "configurator__option--unavailable",
          !hasVariant,
        );
      });
    }

    #findMatchingVariant(selections, selectedValues) {
      if (!PRODUCT.variants) return null;

      const match = PRODUCT.variants.find((v) =>
        this.#variantMatches(selections, v),
      );
      if (match) return match;

      // Fallback: loose includes match (handles sub_options that don't map
      // to a variant dimension — currently-selected values still need to hit
      // some variant for price/image updates).
      return PRODUCT.variants.find((variant) => {
        const variantOptions = (variant.options || []).map((o) =>
          o.toLowerCase().trim(),
        );
        return selectedValues.every((value) =>
          variantOptions.includes(value),
        );
      });
    }

    #updateProductImage() {
      if (!this.productImage || !this.selectedVariant?.featured_image) return;

      const imageUrl = this.selectedVariant.featured_image.src;
      const baseSrc = imageUrl.replace(/(\?|&)(width|height)=\d+/g, "");
      const sep = baseSrc.includes("?") ? "&" : "?";
      const newImageSrc = `${baseSrc}${sep}height=1600`;
      const newSrcset = `${baseSrc}${sep}height=1600 1x, ${baseSrc}${sep}height=2400 2x, ${baseSrc}${sep}height=3200 3x`;

      // Add loading state
      this.productImage.classList.add("configurator__product-image--loading");

      // Preload the new image
      const img = new Image();
      img.onload = () => {
        // Update the image source
        this.productImage.src = newImageSrc;
        this.productImage.srcset = newSrcset;
        this.productImage.alt = this.selectedVariant.title || "Product image";

        // Remove loading state after a small delay to ensure smooth transition
        requestAnimationFrame(() => {
          this.productImage.classList.remove(
            "configurator__product-image--loading",
          );
        });
      };

      img.onerror = () => {
        // Remove loading state even if image fails to load
        this.productImage.classList.remove(
          "configurator__product-image--loading",
        );
      };

      // Start loading the new image
      img.src = newImageSrc;
    }

    #updateOptionImages() {
      if (!PRODUCT.variants) return;

      // Get all checked option inputs (color, seat, model, etc.)
      const checkedInputs = this.querySelectorAll(
        ".configurator__option--option .configurator__option__input:checked",
      );
      const selectedMap = new Map();
      checkedInputs.forEach((input) => {
        selectedMap.set(input.name, input.value.toLowerCase().trim());
      });

      const findVariant = (selections) => {
        let match = PRODUCT.variants.find(
          (v) =>
            v.available !== false && this.#variantMatches(selections, v),
        );
        if (match) return match;
        // Fallback: drop non-variant-dimension constraints and match only on
        // variant dimensions (still position-aware).
        const variantOnly = new Map();
        selections.forEach((value, name) => {
          if (this.optionIndexByName?.has(name)) variantOnly.set(name, value);
        });
        return PRODUCT.variants.find((v) =>
          this.#variantMatches(variantOnly, v),
        );
      };

      // For each option input, find the variant matching current selections
      // but with this option's value, then update its image
      this.querySelectorAll(
        ".configurator__option--option .configurator__option__input",
      ).forEach((input) => {
        const img = input
          .closest("product-configurator-option")
          ?.querySelector(".configurator__option__image");
        if (!img) return;

        const testSelections = new Map(selectedMap);
        testSelections.set(input.name, input.value.toLowerCase().trim());

        const variant = findVariant(testSelections);

        if (variant?.featured_image?.src) {
          const baseSrc = variant.featured_image.src.replace(
            /(\?|&)(width|height)=\d+/g,
            "",
          );
          const sep = baseSrc.includes("?") ? "&" : "?";
          const newSrc = `${baseSrc}${sep}height=400`;
          if (img.src === newSrc) return;
          img.classList.add("configurator__option__image--loading");
          const preload = new Image();
          preload.onload = () => {
            img.src = newSrc;
            img.srcset = `${baseSrc}${sep}height=400 1x, ${baseSrc}${sep}height=800 2x, ${baseSrc}${sep}height=1200 3x`;
            requestAnimationFrame(() => {
              img.classList.remove("configurator__option__image--loading");
            });
          };
          preload.onerror = () => {
            img.classList.remove("configurator__option__image--loading");
          };
          preload.src = newSrc;
        }
      });
    }

    #updateBaseProductPrice() {
      if (!this.selectedVariant?.price) return;

      this.baseProductPrice = Number(this.selectedVariant.price);
      this.#updateTotalPrice();
    }

    #updateProductIdInput() {
      if (!this.productIdInput || !this.selectedVariant?.id) return;

      this.productIdInput.value = this.selectedVariant.id;
    }

    #updateUrlVariant() {
      if (!this.selectedVariant?.id) return;

      const url = new URL(window.location.href);
      url.searchParams.set("variant", this.selectedVariant.id);
      window.history.replaceState({}, "", url.toString());
    }

    #getSubOptionsTotal() {
      let total = 0;
      this.querySelectorAll(
        "product-configurator-option[data-raw-price]",
      ).forEach((option) => {
        const input = option.querySelector("input");
        if (input && input.checked) {
          total += parseInt(option.dataset.rawPrice || "0", 10);
        }
      });
      return total;
    }

    #updateTotalPrice() {
      if (!this.totalPriceElement) return;

      let totalPrice = this.baseProductPrice;

      // Additional products (accessories)
      this.additionalProducts.forEach((product) => {
        const selectedVariant = this.selectedAdditionalVariants.get(product.id);
        totalPrice += selectedVariant ? selectedVariant.price : (product.price || 0);
      });

      // Sub-options (services) with prices
      totalPrice += this.#getSubOptionsTotal();

      const type = this.configuratorType;
      const isAchat = type === "Achat" || type === "";
      const isLeasing = type === "Leasing";

      const priceInCurrency = totalPrice / 100;
      const formattedPurchase = this.priceFormatter
        ? this.priceFormatter.format(priceInCurrency)
        : `${priceInCurrency.toFixed(2)} €`;

      if (isAchat) {
        this.totalPriceElement.textContent = formattedPurchase;
      }
      if (isLeasing && this.purchasePriceSmallElement) {
        const prefix =
          this.purchasePriceSmallElement.dataset.prefix || "Acheter pour ";
        this.purchasePriceSmallElement.textContent = prefix + formattedPurchase;
      }

      this.#updateGreenleaze();
    }

    #updateGreenleaze() {
      if (typeof window.updateGreenleazeConfigurator !== "function") return;

      const variantId = this.selectedVariant?.id || this.productIdInput?.value;
      if (!variantId) return;

      // Accessories: use selected variant price
      let accessoryTotal = 0;
      this.additionalProducts.forEach((product) => {
        const selectedVariant = this.selectedAdditionalVariants.get(product.id);
        accessoryTotal += selectedVariant ? selectedVariant.price : (product.price || 0);
      });

      // Sub-options (services)
      accessoryTotal += this.#getSubOptionsTotal();

      // Pass base product price (without accessories) so data-price stays stable
      // for the external Greenleaze script. Accessories are passed separately.
      window.updateGreenleazeConfigurator(
        this.baseProductPrice,
        variantId,
        accessoryTotal,
      );
    }

    #showNextButtonLoading() {
      if (!this.nextButton) return;
      this.nextButton.classList.add("configurator__next-button--loading");
      this.nextButton.setAttribute("aria-busy", "true");
      if (this.nextButtonLabelElement) {
        this.nextButtonLabelElement.textContent = "Chargement...";
      }
    }

    #hideNextButtonLoading() {
      if (!this.nextButton) return;
      this.nextButton.classList.remove("configurator__next-button--loading");
      this.nextButton.removeAttribute("aria-busy");
      this.#updateNextButtonLabel();
    }

    toggleOptionByHandle(handle, checked) {
      if (!handle) return;

      this.querySelectorAll(
        `.configurator__option-container[handle="${handle}"]`,
      ).forEach((option) => {
        option.classList.toggle(
          "configurator__option-container--hidden",
          checked,
        );

        // Update ARIA attributes
        option.setAttribute("aria-hidden", checked ? "true" : "false");
      });
    }

    /**
     * Vide le panier puis ajoute uniquement les accessoires pour Greenleaze.
     * Le produit principal est ajouté par Greenleaze à la validation (évite les doublons).
     * @returns {Promise<void>}
     */
    async prepareCartForGreenleaze() {
      await fetch("/cart/clear.js", { method: "POST" });

      const items = [];

      const mainVariantId =
        this.selectedVariant?.id || this.productIdInput?.value;
      if (mainVariantId) {
        items.push({ id: mainVariantId, quantity: 1 });
      }

      this.additionalProducts.forEach((product) => {
        const selectedVariant = this.selectedAdditionalVariants.get(product.id);
        const variantId = selectedVariant
          ? selectedVariant.id
          : product.variants?.[0]?.id;
        if (variantId) items.push({ id: variantId, quantity: 1 });
      });

      if (items.length > 0) {
        await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ items }),
        });
      }
    }

  }

  customElements.define("product-configurator", ProductConfigurator);
}

if (!customElements.get("product-configurator-option")) {
  class ProductConfiguratorOption extends HTMLElement {
    constructor() {
      super();
      this.handleChange = this.handleChange.bind(this);
    }

    connectedCallback() {
      this.input = this.querySelector("input");
      this.configurator = this.closest("product-configurator");

      if (!this.input || !this.configurator) {
        return;
      }

      this.input.addEventListener("change", this.handleChange);
    }

    disconnectedCallback() {
      if (this.input) {
        this.input.removeEventListener("change", this.handleChange);
      }
    }

    handleChange(e) {
      this.configurator.updateSelectedVariant();
      this.configurator.recalculateTotalPrice();

      const hideHandle = this.getAttribute("hide");
      if (hideHandle) {
        this.configurator.toggleOptionByHandle(hideHandle, e.target.checked);
      }
    }
  }

  customElements.define(
    "product-configurator-option",
    ProductConfiguratorOption,
  );
}

if (!customElements.get("product-configurator-option-product")) {
  class ProductConfiguratorOptionProduct extends HTMLElement {
    constructor() {
      super();
      this.handleChange = this.handleChange.bind(this);
      this.handleVariantChange = this.handleVariantChange.bind(this);
    }

    connectedCallback() {
      this.input = this.querySelector("input");
      this.configurator = this.closest("product-configurator");
      this.productId = this.getAttribute("product-id");
      this.variantInputs = this.querySelectorAll(
        '.configurator__option__variant-options input[type="radio"]',
      );

      if (!this.input || !this.configurator || !this.productId) {
        return;
      }

      this.input.addEventListener("change", this.handleChange);

      this.variantInputs.forEach((radio) => {
        radio.addEventListener("change", this.handleVariantChange);
      });
    }

    disconnectedCallback() {
      if (this.input) {
        this.input.removeEventListener("change", this.handleChange);
      }
      if (this.variantInputs) {
        this.variantInputs.forEach((radio) => {
          radio.removeEventListener("change", this.handleVariantChange);
        });
      }
    }

    handleChange(e) {
      if (e.target.checked) {
        this.configurator.addAdditionalProduct(this.productId);
        this.#syncSelectedVariant();
      } else {
        this.configurator.removeAdditionalProduct(this.productId);
      }
    }

    handleVariantChange(e) {
      e.stopPropagation();

      // Update active class on the group
      const group = e.target.closest(".configurator__option__variant-options");
      if (group) {
        group.querySelectorAll("label").forEach((l) => {
          l.classList.remove(
            "configurator__option__variant-pill--active",
            "configurator__option__variant-color--active",
          );
        });
        const label = e.target.closest("label");
        if (label) {
          const cls = label.classList.contains(
            "configurator__option__variant-color",
          )
            ? "configurator__option__variant-color--active"
            : "configurator__option__variant-pill--active";
          label.classList.add(cls);
        }
      }

      this.#syncSelectedVariant();
    }

    #syncSelectedVariant() {
      const checkedInputs = this.querySelectorAll(
        '.configurator__option__variant-options input[type="radio"]:checked',
      );
      if (checkedInputs.length === 0) return;

      const product = ALL_ADDITIONAL_PRODUCTS.find(
        (p) => p.id === Number(this.productId),
      );
      if (!product || !product.variants) return;

      // Build selected options indexed by option position
      const selectedOptions = [];
      checkedInputs.forEach((input) => {
        const idx = parseInt(input.dataset.optionIndex, 10);
        selectedOptions[idx] = input.value;
      });

      const variant = product.variants.find((v) =>
        selectedOptions.every(
          (val, i) =>
            val === undefined || (v.options && v.options[i] === val),
        ),
      );

      if (variant) {
        this.configurator.updateAdditionalProductVariant(
          this.productId,
          variant,
        );

        // Update raw price and displayed price for the new variant
        this.dataset.rawPrice = String(variant.price);
        const priceEl = this.querySelector(".configurator__option__price");
        if (priceEl && this.configurator.priceFormatter) {
          const formatted = this.configurator.priceFormatter.format(
            variant.price / 100,
          );
          priceEl.textContent = formatted;
          priceEl.dataset.purchasePrice = formatted;
        }

        // Update accessory image to match selected variant
        const imgEl = this.querySelector(".configurator__option__image");
        if (imgEl && variant.featured_image && variant.featured_image.src) {
          const baseSrc = variant.featured_image.src.replace(
            /(\?|&)(width|height)=\d+/g,
            "",
          );
          const sep = baseSrc.includes("?") ? "&" : "?";
          imgEl.style.opacity = "0.3";
          imgEl.style.transition = "opacity 300ms ease-in-out";
          const preload = new Image();
          preload.onload = () => {
            imgEl.src = `${baseSrc}${sep}height=100`;
            imgEl.srcset = `${baseSrc}${sep}height=100 1x, ${baseSrc}${sep}height=200 2x, ${baseSrc}${sep}height=300 3x`;
            imgEl.alt = variant.title || "";
            requestAnimationFrame(() => {
              imgEl.style.opacity = "";
            });
          };
          preload.src = `${baseSrc}${sep}height=200`;
        }
      }
    }
  }

  customElements.define(
    "product-configurator-option-product",
    ProductConfiguratorOptionProduct,
  );
}

if (!customElements.get("product-configurator-nav-tab")) {
  class ProductConfiguratorNavTab extends HTMLElement {
    constructor() {
      super();
      this.handleClick = this.handleClick.bind(this);
      this.handleKeyDown = this.handleKeyDown.bind(this);
    }

    connectedCallback() {
      this.index = Number(this.getAttribute("index"));
      this.configurator = this.closest("product-configurator");

      if (!this.configurator || isNaN(this.index)) {
        return;
      }

      // Add event listeners
      this.addEventListener("click", this.handleClick);
      this.addEventListener("keydown", this.handleKeyDown);

      // Make keyboard accessible
      if (!this.hasAttribute("tabindex")) {
        this.setAttribute("tabindex", "0");
      }
    }

    disconnectedCallback() {
      this.removeEventListener("click", this.handleClick);
      this.removeEventListener("keydown", this.handleKeyDown);
    }

    handleClick() {
      this.configurator.updateStep(this.index);
    }

    handleKeyDown(e) {
      // Allow Enter and Space to activate the tab
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        this.configurator.updateStep(this.index);
      }
    }
  }

  customElements.define(
    "product-configurator-nav-tab",
    ProductConfiguratorNavTab,
  );
}

if (!customElements.get("product-configurator-next-button")) {
  class ProductConfiguratorNextButton extends HTMLElement {
    constructor() {
      super();
      this.handleClick = this.handleClick.bind(this);
    }

    connectedCallback() {
      this.configurator = this.closest("product-configurator");

      if (!this.configurator) {
        return;
      }

      this.addEventListener("click", this.handleClick);

      // Ensure proper button semantics
      if (!this.hasAttribute("role")) {
        this.setAttribute("role", "button");
      }
      if (!this.hasAttribute("tabindex")) {
        this.setAttribute("tabindex", "0");
      }
    }

    disconnectedCallback() {
      this.removeEventListener("click", this.handleClick);
    }

    handleClick() {
      // En mode Leasing sur la dernière étape, ouvrir la popup Greenleaze
      const isLeasing = this.configurator.configuratorType === "Leasing";
      const isLastStep =
        this.configurator.currentStep === this.configurator.navTabs.length;

      if (isLeasing && isLastStep) {
        const greenleazeButton = document.getElementById(
          "greenleaze-configurator-button",
        );

        if (greenleazeButton) {
          this.#clearGreenleazeError();
          this.#showLouerLoading();
          const onError = (e) => {
            this.#hideLouerLoading();
            this.#showGreenleazeError(e.detail?.message);
          };
          window.addEventListener("greenleazeRedirectError", onError, {
            once: true,
          });
          greenleazeButton.dispatchEvent(
            new MouseEvent("click", { bubbles: true, cancelable: true }),
          );
          return;
        }
      }

      // Comportement par défaut : passer à l'étape suivante ou soumettre
      this.configurator.goToNextStep();
    }

    #showLouerLoading() {
      this.classList.add("configurator__next-button--loading");
      this.setAttribute("aria-busy", "true");
      const label = this.querySelector(".content");
      if (label) label.textContent = "Chargement...";
    }

    #hideLouerLoading() {
      this.classList.remove("configurator__next-button--loading");
      this.removeAttribute("aria-busy");
      const label = this.querySelector(".content");
      if (label) {
        const type = this.configurator?.configuratorType || "";
        const isLeasingOrLocation = type === "Leasing" || type === "Location";
        label.textContent = isLeasingOrLocation ? "Louer" : (this.configurator?.defaultNextLabel || "Suivant");
      }
    }

    #showGreenleazeError(message) {
      const el = document.getElementById("configurator-greenleaze-error");
      if (!el) return;
      el.textContent = message || "Une erreur s'est produite.";
      el.removeAttribute("hidden");
    }

    #clearGreenleazeError() {
      const el = document.getElementById("configurator-greenleaze-error");
      if (!el) return;
      el.textContent = "";
      el.setAttribute("hidden", "");
    }
  }

  customElements.define(
    "product-configurator-next-button",
    ProductConfiguratorNextButton,
  );
}

/* Greenleaze configurator: mise à jour du prix leasing et clic → send-card + redirection */
(function () {
  function getInitialVariantId() {
    const btn = document.getElementById("greenleaze-configurator-button");
    return btn && btn.dataset.variantId ? btn.dataset.variantId : "0";
  }

  window.updateGreenleazeConfigurator = function (
    basePriceTTC,
    variantId,
    accessoryPriceTTC,
  ) {
    const button = document.getElementById("greenleaze-configurator-button");
    if (!button) return;

    if (typeof basePriceTTC !== "undefined") {
      button.dataset.price = String(Math.round(basePriceTTC));
    }
    if (typeof accessoryPriceTTC !== "undefined") {
      button.dataset.accessoryPrice = String(Math.round(accessoryPriceTTC));
    }
    if (typeof variantId !== "undefined") {
      button.dataset.variantId = String(variantId);
    }

    const basePrice = parseInt(button.dataset.price || "0", 10);
    const accessoryPrice = parseInt(button.dataset.accessoryPrice || "0", 10);
    const vid = button.dataset.variantId || getInitialVariantId();

    if (
      !window.initPriceGreenleaze ||
      typeof getAllPricesForProduct !== "function"
    ) {
      return;
    }

    // Lock the initial base price on first successful call to prevent
    // disproportionate tier boundary jumps when options change the variant price
    if (!button.dataset.initialBasePrice) {
      button.dataset.initialBasePrice = String(basePrice);
    }
    const initialBasePrice = parseInt(button.dataset.initialBasePrice, 10);

    // Always use initial base price for tier lookup
    const priceHT = initialBasePrice / 1.2 / 100;
    const pricesForProduct = getAllPricesForProduct(priceHT);
    if (!pricesForProduct) return;

    const durations = Object.keys(pricesForProduct)
      .map(Number)
      .filter((d) => pricesForProduct[d])
      .sort((a, b) => a - b);
    if (!durations.length) return;
    const defaultDuration = durations[durations.length - 1];

    // Durée par défaut : métadonnée custom si valide, sinon la plus longue disponible
    const VALID_DURATION_LABELS = ["6 mois", "12 mois", "24 mois", "36 mois"];
    const metafieldLabel = (button.dataset.leasingDuration || "").trim();
    let chosenDuration = defaultDuration;
    if (metafieldLabel && VALID_DURATION_LABELS.includes(metafieldLabel)) {
      const parsedDuration = parseInt(metafieldLabel, 10);
      if (durations.includes(parsedDuration)) {
        chosenDuration = parsedDuration;
      }
    }

    const baseMonthly = parseFloat(pricesForProduct[chosenDuration].monthly);

    const configType = button.dataset.configuratorType || "";
    const isLeasingOrLocation =
      configType === "Leasing" || configType === "Location";

    // Variant price delta (e.g. colour/size upgrade) — proportional to base rate
    const initialPriceHT = initialBasePrice / 1.2 / 100;
    const ratePerEuroHT = initialPriceHT > 0 ? baseMonthly / initialPriceHT : 0;
    const variantDeltaTTC = Math.max(0, basePrice - initialBasePrice);
    const variantDeltaMonthly = (variantDeltaTTC / 1.2 / 100) * ratePerEuroHT;

    // Accessory monthly: use Greenleaze real pricing instead of proportional estimate
    let accessoryMonthly = 0;
    if (accessoryPrice > 0) {
      const accessoryPriceHT = accessoryPrice / 1.2 / 100;
      const pricesForAccessories = getAllPricesForProduct(accessoryPriceHT);
      if (pricesForAccessories && pricesForAccessories[chosenDuration]) {
        accessoryMonthly = parseFloat(pricesForAccessories[chosenDuration].monthly);
      }
    }

    const totalMonthly = Math.round(baseMonthly + variantDeltaMonthly + accessoryMonthly);

    if (isLeasingOrLocation) {
      button.textContent = totalMonthly + " €/mois";
      const totalPriceElement = document.getElementById("totalPrice");
      if (totalPriceElement) {
        totalPriceElement.style.display = "none";
      }
    } else {
      button.textContent = "Louer à partir de " + totalMonthly + " €/mois";
    }

    // Update individual accessory price labels
    updateAccessoryPriceLabels(isLeasingOrLocation, chosenDuration);

    let trackingId =
      window.greenleaze_tracking ||
      (window.ShopifyAnalytics &&
        window.ShopifyAnalytics.lib &&
        window.ShopifyAnalytics.lib.user &&
        window.ShopifyAnalytics.lib.user().traits &&
        window.ShopifyAnalytics.lib.user().traits().uniqToken);
    if (!trackingId) {
      window.greenleaze_tracking =
        typeof self !== "undefined" && self.crypto && self.crypto.randomUUID
          ? self.crypto.randomUUID()
          : "config-" + Date.now();
      trackingId = window.greenleaze_tracking;
    }
    window._greenleazeConfiguratorDuration = chosenDuration;
    window._greenleazeConfiguratorTrackingId = trackingId;
    button.dataset.greenleazeDuration = String(chosenDuration);
  };

  function updateAccessoryPriceLabels(isLeasingOrLocation, chosenDuration) {
    const options = document.querySelectorAll(
      "product-configurator-option-product",
    );
    options.forEach(function (option) {
      const priceEl = option.querySelector(".configurator__option__price");
      if (!priceEl) return;

      if (!isLeasingOrLocation) {
        // Restore purchase price
        const purchasePrice = priceEl.dataset.purchasePrice;
        if (purchasePrice) priceEl.textContent = purchasePrice;
        return;
      }

      const rawPrice = parseInt(option.dataset.rawPrice || "0", 10);
      if (rawPrice <= 0 || typeof getAllPricesForProduct !== "function") return;

      const priceHT = rawPrice / 1.2 / 100;
      const prices = getAllPricesForProduct(priceHT);
      if (prices && prices[chosenDuration]) {
        const monthly = Math.round(parseFloat(prices[chosenDuration].monthly));
        priceEl.textContent = "à partir de " + monthly + " €/mois";
      }
    });
  }

  window.addEventListener("greenleazePriceActualize", function () {
    const button = document.getElementById("greenleaze-configurator-button");
    if (button) {
      const price = parseInt(button.dataset.price || "0", 10);
      const vid = button.dataset.variantId || getInitialVariantId();
      const accessoryPrice = parseInt(button.dataset.accessoryPrice || "0", 10);
      window.updateGreenleazeConfigurator(price, vid, accessoryPrice);
    }
  });

  document.addEventListener("click", function (e) {
    if (!e.target.closest("#greenleaze-configurator-button")) return;
    e.preventDefault();

    const button = document.getElementById("greenleaze-configurator-button");
    const configType = (button && button.dataset.configuratorType) || "";
    const isLeasingOrLocation =
      configType === "Leasing" || configType === "Location";
    const isAchat = configType === "Achat" || configType === "";
    const redirectUrl = button && button.dataset.associatedProductUrl;

    if (isAchat && redirectUrl) {
      window.location.href = redirectUrl;
      return;
    }
    if (isLeasingOrLocation && e.isTrusted) {
      return;
    }

    // Priorité : data du bouton (mis à jour à chaque changement de prix), puis window
    let duration =
      parseInt(button.dataset.greenleazeDuration, 10) ||
      window._greenleazeConfiguratorDuration;
    let trackingId =
      window._greenleazeConfiguratorTrackingId || window.greenleaze_tracking;
    if (!duration) {
      return;
    }
    if (!trackingId) {
      window.greenleaze_tracking =
        typeof self !== "undefined" && self.crypto && self.crypto.randomUUID
          ? self.crypto.randomUUID()
          : "config-" + Date.now();
      trackingId = window.greenleaze_tracking;
    }

    function sendCartAndRedirectToGreenleaze() {
      fetch(
        window.Shopify && window.Shopify.routes
          ? window.Shopify.routes.root + "cart.js"
          : "/cart.js",
      )
        .then(function (r) {
          return r.json();
        })
        .then(function (cart) {
          if (cart && cart.items) {
            cart.items.forEach(function (item) {
              if (item.image && item.image.startsWith("//")) {
                item.image = "https:" + item.image;
              }
            });
          }
          return fetch("/apps/greenleaze-proxy/send-card", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Access-Control-Allow-Origin": "*",
            },
            body: JSON.stringify({
              cartContents: JSON.stringify(cart),
              duration: duration,
              durationUnit: "monthly",
              fixedDuration: button.dataset.forcedDuration === "true",
              trackingId: trackingId,
              trackingData: null,
              postHogtrackingId: null,
            }),
          });
        })
        .then(function (res) {
          if (!res.ok) {
            throw new Error("Une erreur s'est produite, veuillez réessayer.");
          }
          return res.json().then(function (data) {
            return { ok: res.ok, data: data };
          });
        })
        .then(function (result) {
          if (result.data && result.data.redirectUrl) {
            let url = result.data.redirectUrl;
            // Forcer le paramètre duration uniquement si le metafield forced_duration est activé
            if (button.dataset.forcedDuration === "true") {
              const durationParam = "duration=" + encodeURIComponent(duration);
              const durationRegex = /[?&]duration=[^&]*/;
              if (durationRegex.test(url)) {
                url = url.replace(durationRegex, (m) =>
                  m.startsWith("?") ? "?" + durationParam : "&" + durationParam,
                );
              } else {
                url += (url.includes("?") ? "&" : "?") + durationParam;
              }
            }
            window.location.href = url;
          } else {
            const msg =
              (result.data && result.data.message) ||
              "Une erreur s'est produite.";
            window.dispatchEvent(
              new CustomEvent("greenleazeRedirectError", {
                detail: { message: msg },
              }),
            );
            if (typeof window.postMessage === "function") {
              window.postMessage(
                { type: "greenleazeError", message: msg },
                "*",
              );
            }
          }
        })
        .catch(function (err) {
          const msg = (err && err.message) || "Une erreur s'est produite.";
          window.dispatchEvent(
            new CustomEvent("greenleazeRedirectError", {
              detail: { message: msg },
            }),
          );
        });
    }

    const configurator = document.querySelector("product-configurator");
    if (configurator && configurator.prepareCartForGreenleaze) {
      configurator
        .prepareCartForGreenleaze()
        .then(sendCartAndRedirectToGreenleaze)
        .catch(function (err) {
          const msg = (err && err.message) || "Erreur préparation panier.";
          window.dispatchEvent(
            new CustomEvent("greenleazeRedirectError", {
              detail: { message: msg },
            }),
          );
        });
    } else {
      fetch("/cart/clear.js", { method: "POST" })
        .then(sendCartAndRedirectToGreenleaze)
        .catch(function (err) {
          const msg = (err && err.message) || "Erreur vidage panier.";
          window.dispatchEvent(
            new CustomEvent("greenleazeRedirectError", {
              detail: { message: msg },
            }),
          );
        });
    }
  });
})();
